<template>
  <div v-if="dataStore.isLoading" class="loading-overlay">
    <div class="progress-container">
      <div class="progress-text">{{ dataStore.progressText }}</div>
      <div v-if="dataStore.isUploading">
        <ProgressBar 
          mode="indeterminate"
          class="custom-indeterminate-progress"
          :style="{
            backgroundColor: '#475569',
            height: '2.5rem',
            borderRadius: '1.5rem',
            overflow: 'hidden',
            position: 'relative'
          }">
        </ProgressBar>
      </div>
      <div v-if="dataStore.isAnalyzing">
        <ProgressBar 
          :value="dataStore.progressAnalyzing" 
          unstyled
          :pt="{
            root: {
              style: {
                'background-color': '#475569',
                'height': '2.5rem',
                'border-radius': '1.5rem',
                'position': 'relative'
              }
            },
            value: {
              style: {
                'background-color': '#0ea5e9',
                'height': '2.5rem',
                'border-radius': '1.5rem'
              }
            },
            label: {
              style: {
                'color': 'white',
                'font-size': '1.5rem',
                'font-weight': '600',
                'text-align': 'center',
                'line-height': '2.5rem',
                'position': 'absolute',
                'width': '100%',
                'top': '0'
              }
            }
          }">
            {{ dataStore.analyzedFiles }} / {{ dataStore.totalFiles }}
        </ProgressBar>
      </div>
    </div>
  </div>

  <div class="file-container">
    <div v-if="dataStore.error" class="error-box">
      <h2 class="heading">{{ t('file_control.heading') }}</h2>
      <FileNotices tone="error" />
      <button @click="dataStore.closeSession() && dataStore.clearError()" class="reload-button">{{ t('file_control.reset_page') }}</button>
    </div>
    <div v-else class="file-control">
      <div class="header">
        <div class="file-control-header">
          <h2 class="heading">{{ t('file_control.heading') }}</h2>
          <button 
            v-tooltip.hover="{
              autoHide: false,
              escape: false,
              value: t('file_control.info_text'),
              pt: {
                root: {
                  style: {
                    'pointer-events': 'auto',
                    'userSelect':     'text',
                    'max-width':      '500px',
                    'color':          '#fff',
                    'font-family':    'Open Sans, sans-serif',
                    'font-size':      '0.9rem',
                    'white-space':    'normal',
                  }
                }
              }
            }"
            class="info-tooltip-button"
          >
            <img src="@/assets/infoIcon.svg" alt="info icon" class="info-tooltip-icon">
          </button>
        </div>

        <div @click="triggerFileInput()"
          :class="dataStore.dropfiles ? 'draganddrop-area-blue' : 'draganddrop-area-gray'"
          @dragover.prevent @drop.prevent="dataStore.handleDrop">
          <input type="file" ref="fileInputRef" @change="dataStore.handleClick" style="display: none;" multiple>
          <div
            :class="dataStore.dropfiles ? 'draganddrop-content-blue' : 'draganddrop-content-gray'"
          >
          </div>
          <div v-if="!dataStore.allfiles.length">
            <div v-html="t('file_control.drag_and_drop')"></div>
          </div>
          <div v-else-if="dataStore.allfiles.length === 1 && dataStore.dropfiles">
            <div v-html="t('file_control.file_selected_click_add', { count: dataStore.allfiles.length })"></div>
          </div>
          <div v-else-if="dataStore.allfiles.length > 1 && dataStore.dropfiles">
            <div v-html="t('file_control.files_selected_click_add', { count: dataStore.allfiles.length })"></div>
          </div>
          <div v-else-if="dataStore.allfiles.length === 1 && !dataStore.dropfiles">
            <div v-html="t('file_control.file_selected_reset', { count: dataStore.allfiles.length })"></div>
          </div>
          <div v-else-if="dataStore.allfiles.length > 1 && !dataStore.dropfiles">
            <div v-html="t('file_control.files_selected_reset', { count: dataStore.allfiles.length })"></div>
          </div>
        </div>
        <div class="file-control-btn-row">
          <div v-if="!dataStore.isSaveBTNclickedonce" class="threshold-wrapper">
            <input
              id="thresholdInput"
              v-model="dataStore.maxTraTimeSec"
              class="threshold-input"
              type="number"
              min="1"
              step="1"
              placeholder="10"
            />
            <button
              type="button"
              class="threshold-info-btn info-tooltip-button"
              v-tooltip.hover="{
                autoHide: false,
                escape: false,
                value: t('file_control.threshold_input'),
                pt: {
                  root: {
                    style: {
                      'pointer-events': 'auto',
                      'userSelect':     'text',
                      'max-width':      '320px',
                      'color':          '#fff',
                      'font-family':    'Open Sans, sans-serif',
                      'font-size':      '0.85rem',
                      'white-space':    'normal'
                    }
                  }
                }
              }"
            >
              <img src="@/assets/infoIcon.svg" alt="info icon" class="info-tooltip-icon">
            </button>
          </div>
          <div class="file-control-actions">
            <!-- Reset Button -->
            <div class="file-reset-btn">
              <div v-if="dataStore.resetValue === 'RESET FILES'" class="file-reset-btn-inner"
                @click="dataStore.resetFiles()">
                {{ dataStore.resetValue }}
              </div>
              <div v-if="dataStore.resetValue === 'RESET ALL'" class="file-reset-btn-inner"
                @click="dataStore.closeSession()">
                {{ dataStore.resetValue }}
              </div>
            </div>
            <!-- Upload Button -->
            <button @click="handleButtonClick"
              :class="dataStore.enableBTN ? 'file-upload-btn-blue' : 'file-upload-btn-gray'">
              <div class="file-upload-btn-inner">
                {{ t('file_control.upload') }}
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Non-blocking notices: skipped file names, files whose analysis failed -->
      <FileNotices v-if="dataStore.hasNotices" tone="warning" class="file-notices-inline" />

      <div v-if="dataStore.isSaveBTNclickedonce" class="uploaded_files">
        <div class="uploaded_files-header">
          <p class="uploaded_files-text">{{ t('file_control.uploaded_files') }}</p>
          <span
            v-if="dataStore.changeFileSelection !== ''"
            class="remove-selection-btn"
            @mouseover="dataStore.hovered = true"
            @mouseleave="dataStore.hovered = false"
            @click="dataStore.toggleFile('')">
            {{ t('file_control.remove_selection') }}
          </span>
        </div>
      </div>

      <div v-if="dataStore.isSaveBTNclickedonce" class="files">
        <div class="uploaded_files_grid">
          <div
            v-for="(file, index) in dataStore.allfiles"
            :key="index"
            class="file-item"
            :class="{
              'file-selected': dataStore.changeFileSelection === file.name,
              'file-failed': dataStore.failedNameIds.has(file.name)
            }">
            <p
              class="show-files-btn"
              @mouseover="file.hovered = true"
              @mouseleave="file.hovered = false"
              @click="dataStore.toggleFile(file.name)">
              {{ getDisplayFileName(file.name) }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useDataStore } from '@/stores/dataStore.js'
import ProgressBar from 'primevue/progressbar'
import FileNotices from '@/components/FileNotices.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const dataStore = useDataStore()
const fileInputRef = ref(null)

const triggerFileInput = () => {
  fileInputRef.value.click()
}

const getDisplayFileName = (filename) => {
  return filename.split('_').slice(1).join('_')
}

const handleButtonClick = async () => {
  await dataStore.saveFile()
  dataStore.enableBTN = false
  dataStore.dropfiles = false
  dataStore.resetValue = 'RESET ALL'
}
</script>

<style>
.custom-indeterminate-progress .p-progressbar-value {
  background-color: #0ea5e9 !important;
}
</style>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 998;
}

.progress-container {
  background-color: var(--box-background-color);
  border-radius: 8px;
  padding: 40px;
  width: 600px;
  min-height: 200px;
  position: relative;
  flex-direction: column;
  max-width: 90%;
  max-height: 90%;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
  color: #179DE0
}

.progress-text {
  max-width: fit-content;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 30px;
  padding-top: 10px;
  font-weight: bold;
  font-size: 30px;
  color: var(--text-primary-color);
}

.error-box {
  background-color: var(--errorbox-background-color);
  border-radius: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 0 auto;
}

.file-notices-inline {
  margin: 0 15px 15px 15px;
}

.reload-button {
  background-color: var(--reload-button-background-color);
  font-size: 16px;
  color: var(--reload-button-text-color);
  border: none;
  border-radius: 5px;
  padding: 10px 20px;
  cursor: pointer;
  align-self: flex-end;
}

.reload-button:hover {
  background-color: var(--reload-button-background-color-hover);
  transition: 0.2s;
}

.file-container {
  height: 100%;
}

.header {
  padding: 15px;
}

.file-control {
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  background-color: var(--background-color-secondary);
  padding: 15px;
  height: 100%;
}

.file-control-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
}

.file-control-info-text {
  font-size: 12px;
}

.file-upload {
  height: 100%;
  padding: 15px;
  color: var(--text-primary-color)
}

.info-tooltip-button {
  padding: 0;
  background-color: transparent;
  border: none;
  cursor: pointer;
}

.info-tooltip-icon {
  height: 21px;
  width: 21px;
}

ul {
  padding-left: 20px;
  margin-left: 0;
  font-size: 12px;
  line-height: 15px;
}

li {
  padding-left: 0;
  margin-left: 0;
}

.bold {
  font-weight: bold;
}

h2 {
  font-size: 16px;
  font-weight: 700;
  padding-bottom: 3px;
  margin: 0;

}

p {
  font-size: 12px;
  line-height: 15px;
  font-weight: 500;
}

.draganddrop-area-blue {
  border: 1px solid var(--dragAndDrop-blue-border-color);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  border-radius: 4px;
  height: 70px;
  width: 100%;
  background-color: var(--dragAndDrop-blue-background-color);
  color: var(--dragAndDrop-blue-text-color);
  gap: 10px;
  cursor: pointer;
  pointer-events: painted;
  padding: 0 10px
}

.draganddrop-area-gray {
  border: 1px solid var(--dragAndDrop-grey-border-color);
  display: flex;
  justify-content: center;
  pointer-events: none;
  align-items: center;
  flex-direction: row;
  border-radius: 4px;
  height: 70px;
  width: 100%;
  background-color: var(--dragAndDrop-grey-background-color);
  color: var(--dragAndDrop-grey-text-color);
  cursor: no-drop;
  gap: 10px;
  padding: 0 10px
}

.draganddrop-content-blue {
  height: 16px;
  width: 16px;
  background-image: url('@/assets/DaD_img.png');
  background-repeat: no-repeat;
  flex-shrink: 0;
}

.draganddrop-content-gray {
  height: 24px;
  width: 24px;
  background-image: url('@/assets/DaD_gray.png');
  background-repeat: no-repeat;
  flex-shrink: 0;
}

.file-control-btn-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  margin-top: 15px;
}

.file-control-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

.threshold-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.threshold-input {
  background: var(--searchbar-bg);
  color: var(--searchbar-text);
  border: 1px solid var(--searchbar-border);
  border-radius: 6px;
  height: 38px;
  width: 75px;
  /* min-width: 100px; */
  padding: 10px 34px 10px 10px;
  margin-top: 0;
  font-size: 1rem;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}

.threshold-input::-webkit-outer-spin-button,
.threshold-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.threshold-input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.threshold-input::placeholder {
  color: var(--searchbar-placeholder-color);
}

.threshold-input:hover {
  border-color: var(--searchbar-border-hover);
}

.threshold-input:focus {
  outline: none;
  border-color: var(--searchbar-focus-border);
  background: var(--searchbar-bg);
  color: var(--searchbar-text);
}

.threshold-info-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  padding: 0;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.threshold-info-btn .info-tooltip-icon {
  height: 16px;
  width: 16px;
}

@media (max-width: 1720px) {
  .file-control-btn-row {
    flex-direction: column;
    align-items: stretch;
  }

  .threshold-wrapper {
    width: 100%;
  }

  .threshold-input {
    width: 100%;
    min-width: 0;
  }

  .file-control-actions {
    margin-left: 0;
    justify-content: flex-end;
  }
}

.file-upload-btn-gray {
  background-color: var(--uploadButton-grey-background-color);
  display: flex;
  align-items: center;
  border-radius: 6px;
  border: none;
  cursor: not-allowed;
  pointer-events: none;
}

.file-upload-btn-blue {
  background-color: var(--uploadButton-blue-background-color);
  display: flex;
  align-items: center;
  border-radius: 6px;
  border: none;
  cursor: pointer;
}

.file-upload-btn-blue:hover {
  background-color: var(--uploadButton-blue-background-color-hover);
  color: var(--uploadButton-blue-text-color-hover);
  transition: 0.2s;
}

.file-upload-btn-inner {
  font-weight: 700;
  font-size: 13px;
  color: var(--uploadButton-blue-text-color);
  margin: 8px 15px 8px 15px;
}

.file-reset-btn {
  display: flex;
  align-items: center;
  background-color: var(--resetbutton-background-color);
  font-weight: bolder;
  color: var(--resetbutton-text-color);
  font-size: 13px;
  cursor: pointer;
}

.file-reset-btn-inner {
  padding: 10px 15px;
  cursor: pointer;
  pointer-events: painted;
  white-space: nowrap;
}

.uploaded_files-container {
  overflow-x: hidden;
}

.files {
  overflow: overlay;
  padding: 0 15px 15px 15px;
}

.files::-webkit-scrollbar {
  width: 10px;
}

.files::-webkit-scrollbar-track {
  background-color: var(--background-color-secondary);
  border-radius: 10px 10px 0 0;
}

.files::-webkit-scrollbar-thumb {
  background-color: var(--scrollbar-thumb);
  border-radius: 10px;
}

.files::-webkit-scrollbar-thumb:hover {
  background-color: var(--scrollbar-thumb-hover);
}

.uploaded_files_grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.uploaded_files .uploaded_files-text {
  font-size: 14px;
  display: inline-block;
  font-weight: 560;
  margin: 0;
  cursor: default;
}

.uploaded_files-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 500;
  padding: 0 15px 10px 15px;
}

.remove-selection-btn {
  color: var(--removeSelectionButton-text-color);
  font-weight: 600;
  cursor: pointer;
  font-size: 12px;
}

.file-item {
  border-radius: 4px;
  display: block;
  cursor: pointer;
}

.file-selected {
  background-color: var(--selectedFile-background-color);
  margin-left: -10px;
  margin-right: 10px;
  padding: 0 10px; 
}

.file-selected .show-files-btn {
  color: var(--selectedFile-text-color);
}

.show-files-btn {
  color: var(--files-text-color);
  padding: 5px 0;
  margin: 0;
}

/* Uploaded file whose analysis failed (details in the notice above the list) */
.file-failed .show-files-btn {
  color: var(--errorbox-text-color);
  text-decoration: line-through;
}
</style>
